import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { api } from '../lib/api';
import { useOturum } from '../lib/oturum';

/**
 * AKTİVİTE KAYDI — şantiyenin tüm iz kaydı, tek akış.
 *
 * NEDEN AYRI EKRAN: iş detayındaki iz kaydı tek işi anlatır. Şef sabah
 * "dün ne oldu" diye bakmak ister; bunun için işleri tek tek açmak zorunda
 * kalmamalı.
 *
 * ⚠️ T4 — bu kayıtlar DEĞİŞTİRİLEMEZ. Ekranda düzenle/sil düğmesi YOKTUR;
 *    olması da veri tabanı tetikleyicisi tarafından reddedilirdi.
 *
 * Saha kullanıcısı YALNIZ KENDİ hareketlerini görür (sunucu filtreliyor) —
 * burada ayrıca gizleme yapmıyoruz, çünkü yetki istemcide savunulmaz.
 */

interface Kayit {
  id: string;
  zaman: string;
  kayitZamani: string;
  tip: string;
  metin: string;
  kisi: string | null;
  temsilMi: boolean;
  jobId: string | null;
  isEmriNo: string | null;
  unite: string | null;
}

/**
 * Olay tipini insan diline çevirir; bilinmeyen tip HAM HALİYLE görünür.
 *
 * Kasıtlı olarak eksiksiz olmaya çalışmıyor: yeni bir olay tipi eklendiğinde
 * ekran bozulmaz, sadece teknik ad görünür. Sessizce "Bilinmeyen" yazmak,
 * iz kaydında bilgi gizlemek olurdu.
 */
const TIP_ETIKET: Record<string, string> = {
  is_acildi: 'İş açıldı',
  kesif_tamamlandi: 'Keşif tamamlandı',
  izin_alindi: 'İş izni alındı',
  izin_override: 'İzin atlandı',
  malzeme_sevk: 'Malzeme sevk edildi',
  kurulum_basladi: 'Kurulum başladı',
  kurulum_bitti: 'Kurulum bitti',
  guvenli_degil: 'GÜVENLİ DEĞİL',
  yesil_kart_kaldirildi: 'Yeşil kart kaldırıldı',
  teslim_edildi: 'Teslim & onay',
  sokum_acildi: 'Söküm açıldı',
  sokum_devam: 'Söküm sürüyor',
  sokum_temiz: 'Söküm — malzeme temiz',
  sokum_iade: 'Söküm — malzeme iade',
  revize_acildi: 'Revize açıldı',
  revize_basladi: 'Revize başladı',
  revize_bitti: 'Revize bitti',
  revize_onaylandi: 'Revize onaylandı',
  rutin_kontrol: 'Haftalık kontrol',
  ertelendi: 'Ertelendi',
  iptal_edildi: 'İş iptal edildi',
  is_kapandi: 'İş kapandı',
  duzeltildi: 'Şef düzeltmesi',
  geri_alindi: 'Geri alındı (yanlış tik)',
  yeniden_planlandi: 'Yeniden akışa alındı',
  foto_eklendi: 'Fotoğraf eklendi',
  foto_gecersiz: 'Fotoğraf geçersiz işaretlendi',
  is_emri_foto_todo: 'İş emri fotoğrafı bekliyor',
  gunluk_rapor: 'Günlük rapor gönderildi',
  kuruldu_maili: 'Müşteriye "kuruldu" maili gitti',
  kuruldu_maili_bekliyor: 'Kuruldu maili kuyrukta',
  mail_alici_yok: 'MAİL GİDEMEDİ — ilgili kişi yok',
  kullanici_olusturuldu: 'Kullanıcı oluşturuldu',
  kullanici_guncellendi: 'Kullanıcı güncellendi',
  hesap_acildi: 'Hesap açıldı',
  hesap_kapatildi: 'Hesap kapatıldı',
  parola_sifirlandi: 'Parola sıfırlandı',
  bootstrap_admin: 'Sistem kuruldu',
  temsil_baslatildi: 'Kullanıcı geçişi başladı',
  temsil_bitirildi: 'Kullanıcı geçişi bitti',
};

/** Dikkat çekmesi gereken olaylar kırmızı, iyi haberler yeşil. */
function renk(tip: string): string {
  if (['guvenli_degil', 'yesil_kart_kaldirildi', 'izin_override', 'mail_alici_yok', 'iptal_edildi'].includes(tip))
    return 'bg-crit';
  if (['teslim_edildi', 'kurulum_bitti', 'is_kapandi', 'revize_onaylandi', 'kuruldu_maili'].includes(tip))
    return 'bg-ok';
  if (['ertelendi', 'is_emri_foto_todo', 'temsil_baslatildi', 'duzeltildi', 'foto_gecersiz'].includes(tip))
    return 'bg-warn';
  return 'bg-wait/40';
}

export function Aktivite() {
  const { ben } = useOturum();
  const navigate = useNavigate();
  const [kayitlar, setKayitlar] = useState<Kayit[] | null>(null);
  const [tip, setTip] = useState('');
  const [ara, setAra] = useState('');
  const [hata, setHata] = useState<string | null>(null);

  useEffect(() => {
    setKayitlar(null);
    api
      .get<{ kayitlar: Kayit[] }>(`/aktivite?limit=200${tip ? `&tip=${tip}` : ''}`)
      .then((d) => setKayitlar(d.kayitlar))
      .catch(() => setHata('Aktivite kaydı yüklenemedi.'));
  }, [tip]);

  const suzulmus = useMemo(() => {
    if (!kayitlar) return null;
    const q = ara.trim().toLocaleLowerCase('tr');
    if (!q) return kayitlar;
    return kayitlar.filter((k) =>
      [k.metin, k.kisi, k.isEmriNo, k.unite]
        .filter(Boolean)
        .some((s) => s!.toLocaleLowerCase('tr').includes(q)),
    );
  }, [kayitlar, ara]);

  /** Gün başlıklarıyla grupla — "dün ne oldu" sorusunun cevabı böyle okunur. */
  const gunler = useMemo(() => {
    if (!suzulmus) return [];
    const m = new Map<string, Kayit[]>();
    for (const k of suzulmus) {
      const g = new Date(k.zaman).toLocaleDateString('tr-TR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        weekday: 'long',
      });
      (m.get(g) ?? m.set(g, []).get(g)!).push(k);
    }
    return [...m.entries()];
  }, [suzulmus]);

  const tipler = useMemo(
    () => [...new Set((kayitlar ?? []).map((k) => k.tip))].sort(),
    [kayitlar],
  );

  return (
    <div className="p-4 lg:p-6">
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <input
          value={ara}
          onChange={(e) => setAra(e.target.value)}
          placeholder="Kayıtlarda ara…"
          className="min-w-52 flex-1 rounded-(--radius-mod) border border-line-2 px-3 py-2 text-sm outline-none focus:border-navy"
        />
        <select
          value={tip}
          onChange={(e) => setTip(e.target.value)}
          className="rounded-(--radius-mod) border border-line-2 bg-white px-3 py-2 text-sm outline-none focus:border-navy"
        >
          <option value="">Tüm olaylar</option>
          {/* Tip listesi filtresiz sorgudan gelir; filtre seçiliyken de sabit kalsın diye
              seçili tip her zaman listede olmalı. */}
          {[...new Set([...tipler, ...(tip ? [tip] : [])])].map((t) => (
            <option key={t} value={t}>
              {TIP_ETIKET[t] ?? t}
            </option>
          ))}
        </select>
      </div>

      {!ben?.sefYetkisi && (
        <p className="mb-4 rounded-(--radius-mod) bg-surface-2 px-4 py-2.5 text-xs text-wait">
          Bu listede <strong>yalnız sizin</strong> hareketleriniz görünür.
        </p>
      )}

      {hata && (
        <p className="rounded-(--radius-mod) bg-crit/10 p-4 text-sm text-crit">{hata}</p>
      )}

      {kayitlar === null && !hata && <p className="py-12 text-center text-sm text-wait">Yükleniyor…</p>}

      {suzulmus?.length === 0 && (
        <p className="py-12 text-center text-sm text-wait">Kayıt yok.</p>
      )}

      {gunler.map(([gun, satirlar]) => (
        <section key={gun} className="mb-6">
          <h2 className="mb-2 text-xs font-semibold tracking-wide text-wait uppercase">{gun}</h2>
          <div className="overflow-hidden rounded-(--radius-mod) border border-line bg-surface shadow-(--shadow-mod)">
            {satirlar.map((k) => (
              <div
                key={k.id}
                onClick={() =>
                  k.jobId && void navigate({ to: '/is/$isId', params: { isId: k.jobId } })
                }
                className={`flex items-start gap-3 border-b border-line/70 px-4 py-3 last:border-0 ${
                  k.jobId ? 'cursor-pointer hover:bg-ground' : ''
                }`}
              >
                <span className="num w-11 shrink-0 pt-0.5 text-xs text-wait">
                  {new Date(k.zaman).toLocaleTimeString('tr-TR', {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </span>
                <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${renk(k.tip)}`} />
                <div className="min-w-0 flex-1">
                  <p className="text-sm leading-snug text-ink">
                    <span className="font-medium">{TIP_ETIKET[k.tip] ?? k.tip}</span>
                    {k.metin && <span className="text-neutral-600"> — {k.metin}</span>}
                  </p>
                  <p className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-wait">
                    {k.kisi && <span>{k.kisi}</span>}
                    {k.temsilMi && (
                      // Temsil altında yapılan işlem — iz kaydında İKİ KİMLİK de durur.
                      <span
                        className="rounded bg-warn/15 px-1.5 py-px text-[10px] font-medium text-warn"
                        title="Bu işlem temsil (kullanıcı değiştirme) altında yapıldı"
                      >
                        temsil
                      </span>
                    )}
                    {k.isEmriNo && <span className="num">{k.isEmriNo}</span>}
                    {k.unite && <span>{k.unite}</span>}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </section>
      ))}

      {kayitlar && kayitlar.length >= 200 && (
        <p className="pb-6 text-center text-xs text-wait">
          Son 200 kayıt gösteriliyor. Daha eskisi için arşiv ekranını kullanın.
        </p>
      )}
    </div>
  );
}
